"use client";

import * as React from "react";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { SEARCH_INPUT_MAX_LENGTH } from "@/util/constants";

type SearchInputProps = Omit<
  React.ComponentProps<typeof Input>,
  "type" | "value" | "onChange"
> & {
  value: string;
  onValueChange: (value: string) => void;
  containerClassName?: string;
};

/**
 * The search input shared by the events, sessions, and contributions pages:
 * magnifying-glass affordance, mobile keyboard attributes, query length cap,
 * and a clear button that returns focus to the input. Search input behavior
 * changes belong here, not on the individual pages.
 */
export function SearchInput({
  value,
  onValueChange,
  className,
  containerClassName,
  ...props
}: SearchInputProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const clear = () => {
    onValueChange("");
    inputRef.current?.focus();
  };

  return (
    <div className={cn("relative", containerClassName)}>
      <Search
        className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
        aria-hidden="true"
      />
      <Input
        ref={inputRef}
        type="search"
        inputMode="search"
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        maxLength={SEARCH_INPUT_MAX_LENGTH}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        className={cn(
          // The custom clear button replaces the WebKit-only native one so
          // every browser gets the same affordance.
          "w-full pr-8 pl-9 [&::-webkit-search-cancel-button]:hidden",
          className,
        )}
        {...props}
      />
      {value ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1 size-7 -translate-y-1/2"
          onClick={clear}
          aria-label="Clear search"
        >
          <X className="size-4" aria-hidden="true" />
        </Button>
      ) : null}
    </div>
  );
}
